/**
 * Mock Data for Emergency Locket Application
 *
 * This file contains mock data for testing UI features without database connection.
 * Use this in development/localhost to test various locket states and scenarios.
 */

// Mock Locket Data
export const mockLockets = {
  // Activated locket with complete emergency info
  DEMO001: {
    locketId: "DEMO001",
    isActivated: true,
    status: "activated",
    scanCount: 42,
    createdAt: new Date("2024-01-15"),
    activatedAt: new Date("2024-01-20"),
    lastScannedAt: new Date("2024-12-20"),
  },

  // Locket that needs setup (sold but not activated)
  DEMO002: {
    locketId: "DEMO002",
    isActivated: false,
    status: "sold",
    scanCount: 0,
    createdAt: new Date("2024-02-10"),
    activatedAt: null,
    lastScannedAt: null,
  },

  // Fully activated locket with minimal info
  DEMO003: {
    locketId: "DEMO003",
    isActivated: true,
    status: "activated",
    scanCount: 8,
    createdAt: new Date("2024-03-05"),
    activatedAt: new Date("2024-03-10"),
    lastScannedAt: new Date("2024-12-15"),
  },

  // Registered but not sold
  DEMO004: {
    locketId: "DEMO004",
    isActivated: false,
    status: "registered",
    scanCount: 0,
    createdAt: new Date("2024-04-01"),
    activatedAt: null,
    lastScannedAt: null,
  },

  // Comprehensive activated locket
  DEMO005: {
    locketId: "DEMO005",
    isActivated: true,
    status: "activated",
    scanCount: 127,
    createdAt: new Date("2024-05-12"),
    activatedAt: new Date("2024-05-18"),
    lastScannedAt: new Date("2024-12-24"),
  },
};

// Mock Customer Data
export const mockCustomers = {
  DEMO001: {
    _id: "customer_demo001",
    locketId: "DEMO001",
    profile: {
      name: "Rajesh Kumar",
      phone: "9876543210",
      email: "rajesh.kumar@example.com",
      address: "123 MG Road, Bangalore, Karnataka 560001",
      preferredLanguage: "both",
      photo: null,
    },
    isActive: true,
    createdAt: new Date("2024-01-20"),
    lastLoginAt: new Date("2024-12-22"),
  },

  DEMO003: {
    _id: "customer_demo003",
    locketId: "DEMO003",
    profile: {
      name: "Priya Sharma",
      phone: "8765432109",
      email: "priya.sharma@example.com",
      address: "456 Park Street, Mumbai, Maharashtra 400001",
      preferredLanguage: "english",
      photo: null,
    },
    isActive: true,
    createdAt: new Date("2024-03-10"),
    lastLoginAt: new Date("2024-12-10"),
  },

  DEMO005: {
    _id: "customer_demo005",
    locketId: "DEMO005",
    profile: {
      name: "Dr. Anil Patel",
      phone: "7654321098",
      email: "anil.patel@example.com",
      address: "789 Sector 18, Noida, Uttar Pradesh 201301",
      preferredLanguage: "hindi",
      photo: null,
    },
    isActive: true,
    createdAt: new Date("2024-05-18"),
    lastLoginAt: new Date("2024-12-23"),
  },
};

// Default sections used across all lockets
const defaultSections = [
  {
    _id: "section_medical",
    name: "Medical Information",
    category: "medical",
    order: 0,
    priority: "high",
    isVisible: true,
    icon: "🏥",
    color: "red",
    isDefault: true,
  },
  {
    _id: "section_emergency",
    name: "Emergency Contacts",
    category: "emergency",
    order: 1,
    priority: "high",
    isVisible: true,
    icon: "📞",
    color: "orange",
    isDefault: true,
  },
  {
    _id: "section_personal",
    name: "Personal Information",
    category: "personal",
    order: 2,
    priority: "medium",
    isVisible: true,
    icon: "👤",
    color: "teal",
    isDefault: true,
  },
  {
    _id: "section_identification",
    name: "Identification",
    category: "identification",
    order: 3,
    priority: "low",
    isVisible: true,
    icon: "🪪",
    color: "blue",
    isDefault: true,
  },
  {
    _id: "section_other",
    name: "Other Information",
    category: "other",
    order: 4,
    priority: "low",
    isVisible: true,
    icon: "ℹ️",
    color: "gray",
    isDefault: true,
  },
];

// Mock Emergency Info Data
export const mockEmergencyInfo = {
  DEMO001: {
    _id: "emergency_demo001",
    customerId: "customer_demo001",
    locketId: "DEMO001",
    sections: [...defaultSections],
    infoItems: [
      {
        _id: "item_001",
        key: "रक्त समूह (Blood Group)",
        value: "O+",
        isVisible: true,
        category: "medical",
        order: 0,
        createdAt: new Date("2024-01-20"),
      },
      {
        _id: "item_003",
        key: "डॉक्टर का नाम (Doctor Name)",
        value: "Dr. Ramesh Menon",
        isVisible: true,
        category: "medical",
        order: 1,
        createdAt: new Date("2024-01-20"),
      },
      {
        _id: "item_004",
        key: "अस्पताल (Hospital)",
        value: "Apollo Hospital, Bannerghatta Road",
        isVisible: true,
        category: "medical",
        order: 2,
        createdAt: new Date("2024-01-20"),
      },
      {
        _id: "item_005",
        key: "एलर्जी (Allergies)",
        value: "Penicillin, Shellfish",
        isVisible: true,
        category: "medical",
        order: 3,
        createdAt: new Date("2024-01-21"),
      },
      {
        _id: "item_006",
        key: "दवाइयां (Medications)",
        value: "Metformin 500mg - Twice daily, Aspirin 75mg - Once daily",
        isVisible: true,
        category: "medical",
        order: 4,
        createdAt: new Date("2024-01-21"),
      },
      {
        _id: "item_007",
        key: "मधुमेह (Diabetes)",
        value: "Type 2 Diabetes - Diagnosed 2020",
        isVisible: true,
        category: "medical",
        order: 5,
        createdAt: new Date("2024-01-21"),
      },
      {
        _id: "item_002",
        key: "Emergency Contact (आपातकालीन संपर्क)",
        value: "Sunita Kumar - 9876543211",
        isVisible: true,
        category: "emergency",
        order: 0,
        createdAt: new Date("2024-01-20"),
      },
      {
        _id: "item_008",
        key: "Insurance Policy Number",
        value: "POL-123456789",
        isVisible: false,
        category: "identification",
        order: 0,
        createdAt: new Date("2024-01-22"),
      },
    ],
    lastUpdated: new Date("2024-12-20"),
    createdAt: new Date("2024-01-20"),
    updatedAt: new Date("2024-12-20"),
  },

  DEMO003: {
    _id: "emergency_demo003",
    customerId: "customer_demo003",
    locketId: "DEMO003",
    sections: [...defaultSections],
    infoItems: [
      {
        _id: "item_009",
        key: "Blood Group",
        value: "B+",
        isVisible: true,
        category: "medical",
        order: 0,
        createdAt: new Date("2024-03-10"),
      },
      {
        _id: "item_010",
        key: "Emergency Contact",
        value: "Rahul Sharma - 8765432110",
        isVisible: true,
        category: "emergency",
        order: 0,
        createdAt: new Date("2024-03-10"),
      },
    ],
    lastUpdated: new Date("2024-03-10"),
    createdAt: new Date("2024-03-10"),
    updatedAt: new Date("2024-03-10"),
  },

  DEMO005: {
    _id: "emergency_demo005",
    customerId: "customer_demo005",
    locketId: "DEMO005",
    sections: [
      ...defaultSections,
      {
        _id: "section_dietary",
        name: "Dietary Requirements",
        category: "dietary",
        order: 5,
        priority: "medium",
        isVisible: true,
        icon: "🥗",
        color: "teal",
        isDefault: false,
      },
    ],
    infoItems: [
      {
        _id: "item_011",
        key: "रक्त समूह (Blood Group)",
        value: "AB+",
        isVisible: true,
        category: "medical",
        order: 0,
        createdAt: new Date("2024-05-18"),
      },
      {
        _id: "item_013",
        key: "डॉक्टर का नाम (Doctor Name)",
        value: "Dr. Vikram Singh",
        isVisible: true,
        category: "medical",
        order: 1,
        createdAt: new Date("2024-05-18"),
      },
      {
        _id: "item_014",
        key: "अस्पताल (Hospital)",
        value: "Fortis Hospital, Sector 62, Noida",
        isVisible: true,
        category: "medical",
        order: 2,
        createdAt: new Date("2024-05-18"),
      },
      {
        _id: "item_015",
        key: "एलर्जी (Allergies)",
        value: "None known",
        isVisible: true,
        category: "medical",
        order: 3,
        createdAt: new Date("2024-05-18"),
      },
      {
        _id: "item_016",
        key: "दवाइयां (Medications)",
        value: "Atorvastatin 20mg, Losartan 50mg",
        isVisible: true,
        category: "medical",
        order: 4,
        createdAt: new Date("2024-05-18"),
      },
      {
        _id: "item_017",
        key: "उच्च रक्तचाप (High BP)",
        value: "Hypertension - Controlled with medication",
        isVisible: true,
        category: "medical",
        order: 5,
        createdAt: new Date("2024-05-18"),
      },
      {
        _id: "item_012",
        key: "Emergency Contact (आपातकालीन संपर्क)",
        value: "Meera Patel - 7654321099, Hospital - 1800-XXX-XXXX",
        isVisible: true,
        category: "emergency",
        order: 0,
        createdAt: new Date("2024-05-18"),
      },
      {
        _id: "item_020",
        key: "पूरा नाम (Full Name)",
        value: "Dr. Anil Patel",
        isVisible: true,
        category: "personal",
        order: 0,
        createdAt: new Date("2024-05-18"),
      },
      {
        _id: "item_021",
        key: "उम्र (Age)",
        value: "62 years",
        isVisible: true,
        category: "personal",
        order: 1,
        createdAt: new Date("2024-05-18"),
      },
      {
        _id: "item_018",
        key: "बीमा कार्ड (Insurance Card)",
        value: "HDFC Health Insurance - Policy: HL-987654",
        isVisible: true,
        category: "identification",
        order: 0,
        createdAt: new Date("2024-05-19"),
      },
      {
        _id: "item_019",
        key: "आधार (Aadhar - Last 4 digits)",
        value: "XXXX XXXX 5678",
        isVisible: false,
        category: "identification",
        order: 1,
        createdAt: new Date("2024-05-19"),
      },
      {
        _id: "item_022",
        key: "Diet Type",
        value: "Vegetarian (No onion/garlic)",
        isVisible: true,
        category: "dietary",
        order: 0,
        createdAt: new Date("2024-05-19"),
      },
      {
        _id: "item_023",
        key: "Food Restrictions",
        value: "Low sodium, Low sugar",
        isVisible: true,
        category: "dietary",
        order: 1,
        createdAt: new Date("2024-05-19"),
      },
    ],
    lastUpdated: new Date("2024-12-23"),
    createdAt: new Date("2024-05-18"),
    updatedAt: new Date("2024-12-23"),
  },
};

/**
 * Mock API Service
 *
 * Use this service to simulate API responses in development.
 * Replace actual API calls with mock API calls when USE_MOCK_DATA is true.
 */
// Mock Admin Data
export const mockAdmin = {
  username: "admin",
  password: "admin123", // Mock accepts any password, but this is the demo credential
  role: "admin",
};

// Mock Admin Lockets List (combines locket and customer data for admin view)
const getMockAdminLockets = () => {
  return Object.values(mockLockets).map((locket) => {
    const customer = mockCustomers[locket.locketId];
    return {
      _id: `locket_${locket.locketId}`,
      locketId: locket.locketId,
      status: locket.status,
      registeredAt: locket.createdAt,
      soldAt: locket.status === "sold" || locket.status === "activated" ? locket.createdAt : null,
      activatedAt: locket.activatedAt,
      scanCount: locket.scanCount || 0,
      lastScannedAt: locket.lastScannedAt,
      customer: customer
        ? {
            name: customer.profile.name,
            phone: customer.profile.phone,
            email: customer.profile.email,
          }
        : null,
      registeredBy: "admin",
      shortUrl: `https://l.dityatechnologies.in/${locket.locketId.slice(0, 8)}`,
      qrCodeImage: null, // QR code is large, skip in mock
      qrCodeGeneratedAt: locket.createdAt,
    };
  });
};

export const mockApiService = {
  // Admin login (matches /api/admin/login)
  adminLogin: (username, password) => {
    // Mock accepts any username/password for testing
    // Real implementation would validate against mockAdmin
    if (!username || !password) {
      return Promise.reject({
        response: {
          status: 400,
          data: { error: "Username and password are required" },
        },
      });
    }

    const mockToken = `mock_admin_token_${Date.now()}`;

    return Promise.resolve({
      data: {
        message: "Admin login successful",
        token: mockToken,
        admin: {
          username: username.toLowerCase(),
          role: "admin",
        },
      },
    });
  },

  // Get admin lockets list (matches /api/admin/lockets)
  getAdminLockets: () => {
    const lockets = getMockAdminLockets();
    return Promise.resolve({
      data: {
        lockets: lockets,
      },
    });
  },

  // Bulk generate lockets (matches /api/admin/locket/generate-bulk)
  bulkGenerateLockets: (count, length) => {
    const generatedIds = [];
    const currentDate = new Date();

    for (let i = 0; i < count; i++) {
      // Generate random locket ID
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let locketId = "";
      for (let j = 0; j < length; j++) {
        locketId += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      // Add to mock data
      mockLockets[locketId] = {
        locketId: locketId,
        isActivated: false,
        status: "registered",
        scanCount: 0,
        createdAt: currentDate,
        activatedAt: null,
        lastScannedAt: null,
      };

      generatedIds.push(locketId);
    }

    return Promise.resolve({
      data: {
        message: `Successfully generated ${count} locket IDs`,
        locketIds: generatedIds,
        urlGeneration: {
          total: count,
          successful: count,
          failed: 0,
        },
      },
    });
  },

  // Register and sell locket (matches /api/admin/locket/register-and-sell)
  registerAndSellLocket: (locketId) => {
    const upperLocketId = locketId.toUpperCase();
    const existingLocket = mockLockets[upperLocketId];

    if (existingLocket) {
      if (existingLocket.status === "sold") {
        return Promise.reject({
          response: {
            status: 400,
            data: { error: "Locket already sold", status: existingLocket.status },
          },
        });
      }

      if (existingLocket.status === "activated") {
        return Promise.reject({
          response: {
            status: 400,
            data: { error: "Locket already activated by customer", status: existingLocket.status },
          },
        });
      }

      // Mark as sold if registered
      if (existingLocket.status === "registered") {
        mockLockets[upperLocketId].status = "sold";
        mockLockets[upperLocketId].soldAt = new Date();

        return Promise.resolve({
          data: {
            message: "Locket marked as sold successfully",
            locket: {
              locketId: upperLocketId,
              status: "sold",
              registeredAt: mockLockets[upperLocketId].createdAt,
              soldAt: mockLockets[upperLocketId].soldAt,
            },
          },
        });
      }
    }

    // Register new locket and mark as sold
    const currentDate = new Date();
    mockLockets[upperLocketId] = {
      locketId: upperLocketId,
      isActivated: false,
      status: "sold",
      scanCount: 0,
      createdAt: currentDate,
      soldAt: currentDate,
      activatedAt: null,
      lastScannedAt: null,
    };

    return Promise.resolve({
      data: {
        message: "Locket registered and marked as sold successfully",
        locket: {
          locketId: upperLocketId,
          status: "sold",
          registeredAt: currentDate,
          soldAt: currentDate,
        },
      },
    });
  },

  // Update locket (matches /api/admin/locket/:locketId/update)
  updateLocket: (locketId, updates) => {
    const upperLocketId = locketId.toUpperCase();
    const locket = mockLockets[upperLocketId];

    if (!locket) {
      return Promise.reject({
        response: {
          status: 404,
          data: { error: "Locket not found" },
        },
      });
    }

    // Update locket status
    if (updates.status) {
      mockLockets[upperLocketId].status = updates.status;
    }

    // Update customer data
    if (!mockCustomers[upperLocketId]) {
      mockCustomers[upperLocketId] = {
        _id: `customer_${upperLocketId}`,
        locketId: upperLocketId,
        profile: {
          name: "",
          phone: "",
          email: "",
          address: "",
          preferredLanguage: "both",
          photo: null,
        },
        isActive: true,
        createdAt: new Date(),
        lastLoginAt: null,
      };
    }

    if (updates.customerName) {
      mockCustomers[upperLocketId].profile.name = updates.customerName;
    }
    if (updates.customerPhone) {
      mockCustomers[upperLocketId].profile.phone = updates.customerPhone;
    }
    if (updates.customerEmail) {
      mockCustomers[upperLocketId].profile.email = updates.customerEmail;
    }

    return Promise.resolve({
      data: {
        message: "Locket updated successfully",
        locket: {
          locketId: upperLocketId,
          status: mockLockets[upperLocketId].status,
          customer: {
            name: mockCustomers[upperLocketId].profile.name,
            phone: mockCustomers[upperLocketId].profile.phone,
            email: mockCustomers[upperLocketId].profile.email,
          },
        },
      },
    });
  },

  // Get locket status (matches /api/locket/:locketId)
  getLocketStatus: (locketId) => {
    const locket = mockLockets[locketId.toUpperCase()];

    if (!locket) {
      return Promise.reject({
        response: {
          status: 404,
          data: { error: "Locket not found" },
        },
      });
    }

    if (locket.status === "registered") {
      return Promise.resolve({
        data: {
          error: "Locket not sold yet",
          message: "This locket has not been sold yet. Please purchase from an authorized dealer.",
        },
        status: 400,
      });
    }

    if (locket.status === "sold" && !locket.isActivated) {
      return Promise.resolve({
        data: {
          needsSetup: true,
          locketId: locket.locketId,
          message: "Please set up your emergency locket",
        },
      });
    }

    if (locket.status === "activated") {
      return Promise.resolve({
        data: {
          isActivated: true,
          locketId: locket.locketId,
          message: "Locket is active",
          scanCount: locket.scanCount,
        },
      });
    }

    return Promise.reject({
      response: {
        status: 400,
        data: { error: "Invalid locket status", status: locket.status },
      },
    });
  },

  // Get public emergency info (matches /api/emergency/:locketId/public)
  getPublicEmergencyInfo: (locketId) => {
    const locket = mockLockets[locketId.toUpperCase()];
    const customer = mockCustomers[locketId.toUpperCase()];
    const emergencyInfo = mockEmergencyInfo[locketId.toUpperCase()];

    if (!locket) {
      return Promise.reject({
        response: {
          status: 404,
          data: { error: "Locket not found" },
        },
      });
    }

    if (!locket.isActivated) {
      return Promise.reject({
        response: {
          status: 400,
          data: {
            error: "Locket not activated",
            needsSetup: true,
            locketId: locket.locketId,
          },
        },
      });
    }

    if (!emergencyInfo) {
      return Promise.reject({
        response: {
          status: 404,
          data: { error: "Emergency information not found" },
        },
      });
    }

    // Group items by section (matching backend getVisibleItemsBySection logic)
    // Priority order: high -> medium -> low
    const priorityOrder = { high: 0, medium: 1, low: 2 };

    const visibleSections = emergencyInfo.sections
      .filter((section) => section.isVisible)
      .sort((a, b) => {
        // First sort by priority (high first)
        const priorityDiff = (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
        if (priorityDiff !== 0) return priorityDiff;
        // Then sort by order
        return a.order - b.order;
      });

    const sections = visibleSections
      .map((section) => {
        const sectionItems = emergencyInfo.infoItems
          .filter((item) => item.isVisible && item.category === section.category)
          .sort((a, b) => a.order - b.order);

        return {
          name: section.name,
          category: section.category,
          priority: section.priority,
          icon: section.icon,
          color: section.color,
          order: section.order,
          items: sectionItems.map((item) => ({
            id: item._id,
            key: item.key,
            value: item.value,
          })),
        };
      })
      .filter((section) => section.items.length > 0);

    return Promise.resolve({
      data: {
        locketId: locket.locketId,
        scanCount: locket.scanCount,
        customer: {
          name: customer?.profile?.name || "Unknown",
          preferredLanguage: customer?.profile?.preferredLanguage || "both",
          photo: customer?.profile?.photo || null,
        },
        sections: sections,
        lastUpdated: emergencyInfo.lastUpdated,
        message: "आपातकालीन जानकारी / Emergency Information",
      },
    });
  },

  // Get emergency info for owner/manage view (matches /api/emergency/:locketId/manage)
  getManageEmergencyInfo: (locketId, token) => {
    const emergencyInfo = mockEmergencyInfo[locketId.toUpperCase()];

    if (!emergencyInfo) {
      return Promise.reject({
        response: {
          status: 404,
          data: { error: "Emergency information not found" },
        },
      });
    }

    if (!token) {
      return Promise.reject({
        response: {
          status: 401,
          data: { error: "No token provided" },
        },
      });
    }

    // Sort sections by priority (high first), then by order
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const sortedSections = emergencyInfo.sections.sort((a, b) => {
      const priorityDiff = (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
      if (priorityDiff !== 0) return priorityDiff;
      return a.order - b.order;
    });

    // Group items by section
    const sectionsWithItems = sortedSections.map((section) => {
      const sectionItems = emergencyInfo.infoItems
        .filter((item) => item.category === section.category)
        .sort((a, b) => a.order - b.order)
        .map((item) => ({
          id: item._id,
          key: item.key,
          value: item.value,
          isVisible: item.isVisible,
          category: item.category,
          order: item.order,
          createdAt: item.createdAt,
        }));

      return {
        id: section._id,
        name: section.name,
        category: section.category,
        order: section.order,
        priority: section.priority,
        isVisible: section.isVisible,
        icon: section.icon,
        color: section.color,
        isDefault: section.isDefault,
        items: sectionItems,
      };
    });

    return Promise.resolve({
      data: {
        locketId: locketId,
        sections: sectionsWithItems,
        lastUpdated: emergencyInfo.lastUpdated,
        fieldSuggestions: {
          medical: [
            "रक्त समूह (Blood Group)",
            "एलर्जी (Allergies)",
            "दवाइयां (Medications)",
            "चिकित्सा स्थिति (Medical Conditions)",
            "मधुमेह (Diabetes)",
            "उच्च रक्तचाप (High BP)",
            "डॉक्टर का नाम (Doctor Name)",
            "अस्पताल (Hospital)",
            "स्वास्थ्य बीमा (Health Insurance)",
          ],
          emergency: [
            "आपातकालीन संपर्क नाम (Emergency Contact Name)",
            "आपातकालीन संपर्क फोन (Emergency Contact Phone)",
            "रिश्ता (Relationship)",
            "डॉक्टर का फोन (Doctor Phone)",
            "अस्पताल संपर्क (Hospital Contact)",
          ],
          personal: ["पूरा नाम (Full Name)", "उम्र (Age)", "पता (Address)", "जन्म तिथि (Date of Birth)", "लिंग (Gender)"],
          identification: [
            "आईडी प्रकार (ID Type)",
            "आईडी नंबर (ID Number)",
            "लाइसेंस नंबर (License Number)",
            "बीमा कार्ड नंबर (Insurance Card Number)",
            "आधार (अंतिम 4 अंक) (Aadhar - Last 4 digits)",
          ],
          other: ["अन्य जानकारी (Other Information)"],
          dietary: ["Diet Type", "Food Restrictions", "Food Allergies"],
        },
      },
    });
  },

  // Update customer profile (matches /api/locket/:locketId/profile PUT)
  updateCustomerProfile: (locketId, token, updates) => {
    const customer = mockCustomers[locketId.toUpperCase()];
    if (!customer || !token) {
      return Promise.reject({
        response: { status: 403, data: { error: "Access denied" } },
      });
    }
    if (updates.name) customer.profile.name = updates.name;
    if (updates.phone) customer.profile.phone = updates.phone;
    if (updates.email) customer.profile.email = updates.email;
    if (updates.address !== undefined) customer.profile.address = updates.address;
    if (updates.preferredLanguage) customer.profile.preferredLanguage = updates.preferredLanguage;
    if (updates.photo !== undefined) customer.profile.photo = updates.photo || null;
    return Promise.resolve({
      data: { message: "Profile updated successfully", profile: customer.profile },
    });
  },

  // Get customer profile (matches /api/locket/:locketId/profile)
  getCustomerProfile: (locketId, token) => {
    const customer = mockCustomers[locketId.toUpperCase()];

    if (!customer) {
      return Promise.reject({
        response: {
          status: 403,
          data: { error: "Access denied" },
        },
      });
    }

    if (!token) {
      return Promise.reject({
        response: {
          status: 401,
          data: { error: "No token provided" },
        },
      });
    }

    return Promise.resolve({
      data: {
        profile: customer.profile,
      },
    });
  },

  // Customer login (matches /api/locket/:locketId/login)
  // Mock accepts any password for activated lockets
  customerLogin: (locketId, password) => {
    const locket = mockLockets[locketId.toUpperCase()];
    const customer = mockCustomers[locketId.toUpperCase()];

    if (!locket) {
      return Promise.reject({
        response: {
          status: 404,
          data: { error: "Locket not found" },
        },
      });
    }

    if (!locket.isActivated) {
      return Promise.reject({
        response: {
          status: 400,
          data: { error: "Locket not activated" },
        },
      });
    }

    if (!customer) {
      return Promise.reject({
        response: {
          status: 404,
          data: { error: "Customer not found" },
        },
      });
    }

    // Mock accepts any password - just return a mock token
    const mockToken = `mock_token_${locketId}_${Date.now()}`;

    return Promise.resolve({
      data: {
        token: mockToken,
        message: "Login successful",
      },
    });
  },

  // Delete locket (matches /api/admin/locket/:locketId)
  deleteLocket: (locketId) => {
    const upperLocketId = locketId.toUpperCase();
    const locket = mockLockets[upperLocketId];
    if (!locket) {
      return Promise.reject({
        response: {
          status: 404,
          data: { error: "Locket not found" },
        },
      });
    }

    const wasActivated = locket.isActivated && mockCustomers[upperLocketId];

    // Delete locket
    delete mockLockets[upperLocketId];

    // If activated, delete customer and emergency info
    if (wasActivated) {
      delete mockCustomers[upperLocketId];
      delete mockEmergencyInfo[upperLocketId];
    }

    return Promise.resolve({
      data: {
        message: "Locket deleted successfully",
        deleted: {
          locket: true,
          customer: wasActivated,
          emergencyInfo: wasActivated,
        },
      },
    });
  },
};

/**
 * Configuration
 * Mock data is only enabled when explicitly set via:
 * 1. Environment variable: REACT_APP_USE_MOCK_DATA=true
 * 2. LocalStorage: localStorage.setItem('use_mock_data', 'true')
 */
export const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === "true";

/**
 * Helper function to check if mock data should be used
 * Returns true if:
 * - REACT_APP_USE_MOCK_DATA environment variable is "true", OR
 * - localStorage has "use_mock_data" set to "true"
 */
export const shouldUseMockData = () => {
  return USE_MOCK_DATA || localStorage.getItem("use_mock_data") === "true";
};

/**
 * Sample locket IDs for testing:
 * - DEMO001: Fully activated locket with comprehensive emergency info across multiple sections
 * - DEMO002: Locket that needs setup (sold but not activated)
 * - DEMO003: Activated locket with minimal info (2 items in 2 sections)
 * - DEMO004: Registered but not sold locket
 * - DEMO005: Comprehensive activated locket with custom "Dietary Requirements" section (doctor profile)
 *
 * Section-based structure:
 * - All lockets have 5 default sections: Medical, Emergency Contacts, Personal, Identification, Other
 * - DEMO005 includes a custom "Dietary Requirements" section to demonstrate custom section functionality
 * - Items are grouped by section category
 * - Sections can be reordered and have visibility toggles
 * - Items within sections can be reordered
 * - Public view only shows visible sections with visible items
 * - Owner view shows all sections (including empty ones) with all items
 */
